import axios from 'axios';
import { supabase } from './supabase';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});
console.log('process env:', process.env);
// Add a request interceptor to add the auth token
api.interceptors.request.use(async (config) => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    
    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }
    
    return config;
  } catch (error) {
    console.error('Error getting session:', error);
    return config;
  }
});

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) throw refreshError;

        if (session?.access_token) {
          originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Error refreshing session:', refreshError);
        // Redirect to login if session refresh fails
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// API endpoints
export const whatsappApi = {
  getStatus: () => api.get('/whatsapp/status'),
  getQR: () => api.get('/whatsapp/qr'),
  connect: () => api.post('/whatsapp/connect'),
  disconnect: () => api.post('/whatsapp/disconnect'),
  sendMessage: (data) => api.post('/whatsapp/send', data),
  sendAvailability: (data) => api.post('/whatsapp/send-availability', data),
  sendOrders: (data) => api.post('/whatsapp/send-orders', data),
};

export const settingsApi = {
  getGmailSettings: () => api.get('/settings/gmail'),
  updateGmailSettings: (data) => api.put('/settings/gmail', data),
  getGmailAuthUrl: () => api.get('/settings/gmail/auth-url'),
};

export const emailApi = {
  syncEmails: () => api.post('/email/sync'),
  getEmails: () => api.get('/email/list')
};

export const conversationsApi = {
  getAll: () => api.get('/conversations'),
  getById: (id) => api.get(`/conversations/${id}`),
  create: (data) => api.post('/conversations', data),
  update: (id, data) => api.put(`/conversations/${id}`, data),
  delete: (id) => api.delete(`/conversations/${id}`),
};

export const messagesApi = {
  getByConversation: (conversationId) => api.get(`/messages/${conversationId}`),
  send: (conversationId, data) => api.post(`/messages/${conversationId}`, data),
};

export const profileApi = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
  updateSettings: (data) => api.put('/profile/settings', data),
};

export const analyticsApi = {
  getStats: () => api.get('/analytics/stats'),
  getMessageHistory: (params) => api.get('/analytics/messages', { params }),
  getConversationMetrics: () => api.get('/analytics/conversations'),
  /** Récupère toutes les données agrégées pour le tableau de bord */
  getDashboard: () => api.get('/analytics/dashboard'),
};

// Nouvelle API pour les boutiques
export const shopsApi = {
  /** Récupère toutes les boutiques (filtrées par RLS côté serveur) */
  getAll: (filters = {}) => api.get('/shops', { params: filters }),
  /** Récupère une boutique par son ID */
  getById: (id) => api.get(`/shops/${id}`),
  /** Crée une nouvelle boutique 
   * @param {Object} data - { name, address?, phone?, website? } 
   */
  create: (data) => api.post('/shops', data),
  /** Met à jour une boutique 
   * @param {string} id - ID de la boutique
   * @param {Object} data - { name?, address?, phone?, website?, is_active? }
   */
  update: (id, data) => api.put(`/shops/${id}`, data),
  /** Supprime une boutique */
  delete: (id) => api.delete(`/shops/${id}`),
};

export const cashiersApi = {
  /** Récupère tous les caissiers (avec shop_id et shop_name) */
  getAll: () => api.get('/cashiers'),
  // getById n'est probablement pas utilisé mais on le laisse au cas où
  getById: (id) => api.get(`/cashiers/${id}`),
  /** Crée un nouveau caissier 
   * @param {Object} data - { name, pinCode, shopId }
   */
  create: (data) => api.post('/cashiers', data),
  /** Met à jour un caissier 
   * @param {string} id - ID du caissier
   * @param {Object} data - { name?, pinCode?, is_active?, shopId? }
   */
  update: (id, data) => api.patch(`/cashiers/${id}`, data),
  /** Authentifie un caissier par PIN 
   * @param {string} pinCode
   * @returns {Promise<AxiosResponse<{ cashier: Object, token: string }>>} - Infos caissier (avec shop_id, shop_name) et token
   */
  login: (pinCode) => api.post('/cashiers/login', { pin_code: pinCode }),
  // delete n'existe pas côté serveur, on le commente ou retire
  // delete: (id) => api.delete(`/cashiers/${id}`)
};

export const integrationsApi = {
  testShopifyConnection: (shop_name, api_key, api_password, api_version) => api.post('/integrations/shopify/test-connection', { shop_name, api_key, api_password, api_version }),
  previewShopifyOrders: (credentials, filters) => api.post('/integrations/shopify/preview-orders', { credentials, filters }),
  checkDuplicateOrders: (orderIds) => api.post('/integrations/orders/check-duplicates', { orderIds }),
  importShopifyOrders: (credentials, filteredOrders) => api.post('/integrations/shopify/import-orders', {credentials, filteredOrders}),
  validateShopifyCredentials: (shopDomain, accessToken) => api.post('/integrations/shopify/validate', { shopDomain, accessToken }),
  fetchShopifyProducts: (shopDomain, accessToken, cursor = null) => api.post('/integrations/shopify/products', { shopDomain, accessToken, cursor }),
  importShopifyProducts: (shopDomain, accessToken, productIds) => api.post('/integrations/shopify/import', { shopDomain, accessToken, productIds }),
  fetchShopifyProductDetails: (productId, shopDomain, accessToken) => api.post(`/integrations/shopify/products/${productId}`, { shopDomain, accessToken }),
  /**
   * Lance l'importation des clients depuis Shopify.
   * @returns {Promise<AxiosResponse<any>>} La réponse axios.
   */
  importShopifyCustomers: () => api.post('/integrations/shopify/import-customers'),
};

export const deliveryPersonsApi = {
  getAll: (filters) => api.get('/delivery-persons', { params: filters }),
  getById: (id) => api.get(`/delivery-persons/${id}`),
  create: (data) => api.post('/delivery-persons', data),
  update: (id, data) => api.put(`/delivery-persons/${id}`, data),
  delete: (id) => api.delete(`/delivery-persons/${id}`),
  // Zones
  getZones: (id) => api.get(`/delivery-persons/${id}/zones`),
  addZone: (id, data) => api.post(`/delivery-persons/${id}/zones`, data),
  deleteZone: (id, zoneId) => api.delete(`/delivery-persons/${id}/zones/${zoneId}`),
  // Disponibilités
  getAvailabilities: (id) => api.get(`/delivery-persons/${id}/availabilities`),
  updateAvailability: (id, data) => api.post(`/delivery-persons/${id}/availabilities`, data),
  // Paiements
  getPayments: (id, filters) => api.get(`/delivery-persons/${id}/payments`, { params: filters }),
  createPayment: (id, data) => api.post(`/delivery-persons/${id}/payments`, data),
  // Statistiques
  getStats: (id) => api.get(`/delivery-persons/${id}/stats`),
  // Livreurs disponibles
  getAvailable: (params) => api.get('/delivery-persons/available', { params }),
  // Documents
  createDocument: (deliveryId, data) => api.post(`/delivery-persons/deliveries/${deliveryId}/documents`, data),
  getDocuments: (deliveryId) => api.get(`/delivery-persons/deliveries/${deliveryId}/documents`),
  updateDocument: (documentId, data) => api.put(`/delivery-persons/documents/${documentId}`, data),
  // Obtenir les statistiques quotidiennes d'un livreur
  getDailyStats: async (deliveryPersonId, date) => {
    try {
      const response = await api.get(`/delivery-persons/stats/daily/${deliveryPersonId}/${date}`);
      return response;
    } catch (error) {
      console.error('Error getting daily stats:', error);
      return null;
    }
  },
  // Envoyer un message à un livreur
  sendMessage: async (deliveryPersonId, data) => {
    try {
      const response = await api.post(`/delivery-persons/${deliveryPersonId}/send-message`, data);
      return response;
    } catch (error) {
      console.error('Error sending message:', error);
      return null;
    }
  }
};

export const ordersApi = {
  // Récupérer toutes les commandes
  getAll: async (filters = {}) => {
    try {
      const response = await api.get('/orders', { params: filters });
      return response;
    } catch (error) {
      console.error('Error getting orders:', error);
      return null;
    }
  },
  
  // Récupérer une commande par son ID
  getById: async (id) => {
    try {
      const response = await api.get(`/orders/${id}`);
      return response;
    } catch (error) {
      console.error('Error getting order:', error);
      return null;
    }
  },
  
  // Créer une nouvelle commande
  create: async (data) => {
    try {
      const response = await api.post('/orders', data);
      return response;
    } catch (error) {
      console.error('Error creating order:', error);
      return null;
    }
  },
  
  // Mettre à jour une commande
  update: async (id, data) => {
    try {
      const response = await api.put(`/orders/${id}`, data);
      return response;
    } catch (error) {
      console.error('Error updating order:', error);
      return null;
    }
  },
  
  // Supprimer une commande
  delete: async (id) => {
    try {
      const response = await api.delete(`/orders/${id}`);
      return response;
    } catch (error) {
      console.error('Error deleting order:', error);
      return null;
    }
  },
  
  // Récupérer les statistiques des commandes
  getStats: async (filters = {}) => {
    try {
      const response = await api.get('/orders/stats', { params: filters });
      return response;
    } catch (error) {
      console.error('Error getting order stats:', error);
      return null;
    }
  },
  
  // Récupérer les commandes récentes (5 dernières)
  getRecent: async (filters = {}) => {
    try {
      const params = { ...filters, limit: 5 };
      const response = await api.get('/orders/recent', { params });
      return response;
    } catch (error) {
      console.error('Error getting recent orders:', error);
      return null;
    }
  },
  
  // Récupérer les zones de livraison
  getZones: async () => {
    try {
      const response = await api.get('/delivery-zones');
      return response;
    } catch (error) {
      console.error('Error getting delivery zones:', error);
      return null;
    }
  },
  
  // Attribuer des commandes à un livreur
  assignOrders: async (orderIds, deliveryPersonId, options = {}) => {
    try {
      const response = await api.post('/orders/assign', {
        order_ids: orderIds,
        delivery_person_id: deliveryPersonId,
        delivery_date: options.delivery_date,
        shipping_fee: options.shipping_fee
      });
      return response;
    } catch (error) {
      console.error('Error assigning orders:', error);
      return null;
    }
  }
};

export const createProduct = async (productData) => {
  try {
    const response = await api.post('/products', productData);
    return response.data;
  } catch (error) {
    console.error('Erreur API createProduct:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Erreur lors de la création du produit via API.');
  }
};

/**
 * Met à jour un produit existant via l'API backend.
 * @param {string} productId - L'ID du produit à mettre à jour.
 * @param {object} productData - Les nouvelles données du produit.
 * @returns {Promise<object>} La réponse de l'API.
 * @throws {Error} Si l'appel API échoue.
 */
export const updateProduct = async (productId, productData) => {
    if (!productId) throw new Error('ID du produit requis pour la mise à jour.');
    try {
        const response = await api.put(`/products/${productId}`, productData);
        return response.data; // Contient { message: '...', data: { productId: ... } }
    } catch (error) {
        console.error('Erreur API updateProduct:', error.response?.data || error.message);
        throw new Error(error.response?.data?.message || 'Erreur lors de la mise à jour du produit via API.');
    }
};

// **NOUVELLE SECTION : API Inventaire**
export const inventoryApi = {
  /**
   * Ajuste le stock d'une variante.
   * @param {Object} data - { variantId, quantity, reason, notes?, shouldTriggerAlerts? }
   * @returns {Promise<AxiosResponse>}
   */
  adjustStock: (data) => api.post('/inventory/adjust-stock', data),

  /**
   * Configure une alerte de stock bas pour une variante.
   * @param {Object} data - { variantId, threshold, isActive, notifyUserIds? }
   * @returns {Promise<AxiosResponse>}
   */
  setupStockAlert: (data) => api.post('/inventory/alert', data),

  /**
   * Synchronise le stock d'un produit Shopify et ses variantes avec le backend.
   * @param {string} productId - ID du produit local.
   * @returns {Promise<AxiosResponse>}
   */
  syncShopifyProductStock: (productId) => api.post(`/inventory/shopify-sync/product/${productId}`),

  /**
   * Synchronise tous les produits Shopify modifiés (avec création automatique).
   * Récupère les produits modifiés depuis la dernière synchronisation et les crée/met à jour localement.
   * @returns {Promise<AxiosResponse>}
   */
  syncAllShopifyProducts: () => api.post('/inventory/shopify-sync/all'),

  /**
   * Récupère les produits avec pagination et filtres pour la gestion du stock.
   * @param {Object} params - Paramètres de requête ({ page, pageSize, searchTerm, filter }).
   * @returns {Promise<AxiosResponse<{ products: Array, totalCount: number, page: number, pageSize: number, totalPages: number }>>}
   */
  getProductsForStock: (params) => api.get('/products/products-for-stock', { params }),

  /**
   * Génère et met à jour les SKU et codes-barres pour les variantes de produits.
   * @param {Object} data - { productId, variantIds? }
   * @returns {Promise<AxiosResponse>}
   */
  generateSkuAndBarcode: (data) => api.post('/inventory/generate-codes', data),
};

export const clientsApi = {
  /**
   * Récupère la liste des clients avec filtres (pagination, recherche).
   * @param {object} filters - Les filtres à appliquer (limit, offset, name, phone).
   * @returns {Promise<AxiosResponse<any>>} La réponse axios.
   */
  getAll: (filters = {}) => api.get('/clients', { params: filters }),

  /**
   * Récupère les détails d'un client par son ID
   * @param {string} id - L'ID du client à récupérer
   * @returns {Promise<AxiosResponse<any>>} La réponse axios.
   */
  getById: (id) => api.get(`/clients/${id}`),

  /**
   * Calcule les points de fidélité pour un client spécifique basé sur ses commandes Shopify.
   * @param {number} customerId - L'ID du client.
   * @returns {Promise<AxiosResponse<any>>} La réponse axios.
   */
  calculateShopifyPoints: (customerId) => api.post(`/clients/${customerId}/calculate-shopify-points`),

  /**
   * Calcule les points de fidélité pour un lot de clients basé sur des filtres.
   * @param {object} filters - Les filtres pour sélectionner les clients (ex: { name: 'John' }).
   * @returns {Promise<AxiosResponse<any>>} La réponse axios.
   */
  calculateShopifyPointsBatch: (filters = {}) => api.post('/clients/calculate-shopify-points/batch', { filters }),

  /**
   * Calcule les points de fidélité pour une liste spécifique d'IDs de clients.
   * @param {number[]} customerIds - Un tableau contenant les IDs des clients.
   * @returns {Promise<AxiosResponse<any>>} La réponse axios.
   */
  calculateShopifyPointsForPage: (customerIds = []) => api.post('/clients/calculate-points/page', { customerIds }),
};

export const productsApi = {
  /**
   * Récupère la liste des produits.
   * @param {object} filters - Filtres éventuels (ex: pagination, recherche).
   * @returns {Promise<AxiosResponse<any>|null>}
   */
  getAll: async (filters = {}) => {
    try {
      const response = await api.get('/products', { params: filters });
      return response;
    } catch (error) {
      console.error('Error getting products:', error);
      return null;
    }
  },
    /**
   * Récupère les produits avec pagination et filtres pour la gestion du catalogue.
   * @param {Object} params - Paramètres de requête ({ page, pageSize, searchTerm, filter }).
   * @returns {Promise<AxiosResponse<{ products: Array, totalCount: number, page: number, pageSize: number, totalPages: number }>>}
   */
    getProductsForCatalog: (params) => api.get('/products/products-for-catalog', { params }),
    
  /**
   * Récupère un produit par son ID.
   * @param {string} id - ID du produit.
   * @returns {Promise<AxiosResponse<any>|null>}
   */
  getById: async (id) => {
    try {
      const response = await api.get(`/products/${id}`);
      return response;
    } catch (error) {
      console.error('Error getting product:', error);
      return null;
    }
  }
};

export const subscriptionApi = {
  /** Récupère les informations du plan actuel de l'utilisateur */
  getCurrentPlan: () => api.get('/subscription/current'),
  /** Met à niveau le plan de l'utilisateur */
  upgradePlan: (newPlan) => api.post('/subscription/upgrade', { newPlan }),
  /** Récupère tous les plans disponibles */
  getAvailablePlans: () => api.get('/subscription/plans'),
  /** Récupère l'historique de facturation */
  getBillingHistory: () => api.get('/subscription/billing-history'),
  /** Récupère les statistiques d'usage (produits, boutiques, etc.) */
  getUsageStats: () => api.get('/subscription/usage-stats'),
};

export const userSettingsApi = {
  /** Récupère les paramètres utilisateur actuel */
  getCurrent: () => api.get('/user-settings'),
  /** Met à jour les paramètres utilisateur */
  update: (data) => api.put('/user-settings', data),
};

export default api;
